import React, { useState, useEffect } from 'react';
import { BarChart3, Coins, Users, Settings, ShoppingBag, ShieldCheck, Wallet, Check, X, Lock, Inbox, Hammer, Download, Map, Store } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const TABS = [
  { id: 'stats',       label: 'Stats',       icon: BarChart3 },
  { id: 'tokenomics',  label: 'Tokenomics',  icon: Coins },
  { id: 'users',       label: 'Users',       icon: Users },
  { id: 'deposits',    label: 'Deposits',    icon: Inbox },
  { id: 'vendors',     label: 'Vendors',     icon: Store },
  { id: 'mint',        label: 'Mint Powers', icon: Hammer },
  { id: 'adjust',      label: 'Adjust (Maker-Checker)', icon: ShieldCheck },
  { id: 'products',    label: 'Products',    icon: ShoppingBag },
  { id: 'roadmap',     label: 'Roadmap',     icon: Map },
  { id: 'settings',    label: 'Rate / Deposit / Bonus', icon: Settings },
];

const TSBAdmin = () => {
  const [tab, setTab] = useState('stats');

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="tsb-admin-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
          TSB Reward System — Admin
        </h1>

        <div className="sticky top-24 md:top-28 z-30 -mx-4 md:-mx-6 px-4 md:px-6 bg-slate-950/95 backdrop-blur flex gap-1 overflow-x-auto border-b border-slate-800 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tsb-admin-tab-${t.id}`}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                  tab === t.id ? 'border-amber-400 text-amber-300' : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'stats' && <StatsTab />}
        {tab === 'tokenomics' && <TokenomicsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'deposits' && <DepositsTab />}
        {tab === 'vendors' && <VendorsTab />}
        {tab === 'mint' && <MintTab />}
        {tab === 'adjust' && <AdjustTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'roadmap' && <RoadmapTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

// ========= Stats =========
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/admin/tsb/stats').then(r => setStats(r.data)); }, []);
  if (!stats) return <div>Loading...</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Users w/ Wallet" value={stats.total_users_with_wallet} />
      <Stat label="Total Orders" value={stats.total_orders} />
      <Stat label="Active Products" value={stats.total_active_products} />
      <Stat label="Pending Adjustments" value={stats.pending_adjustments} color="text-orange-300" />
      <Stat label="Volume (₹)" value={(stats.volume?.total_inr || 0).toLocaleString()} color="text-amber-300" />
      <Stat label="Cash Collected" value={`₹${(stats.volume?.total_cash || 0).toLocaleString()}`} color="text-emerald-300" />
      <Stat label="TSB Burned" value={(stats.volume?.total_tsb || 0).toLocaleString()} color="text-purple-300" />
    </div>
  );
};
const Stat = ({ label, value, color = 'text-white' }) => (
  <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
    <div className="text-xs text-slate-400">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </div>
);

// ========= Tokenomics =========
const TokenomicsTab = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get('/admin/tsb/tokenomics').then(r => setT(r.data)); }, []);
  if (!t) return <div>Loading...</div>;
  const pct = (n, d) => d > 0 ? ((n / d) * 100).toFixed(2) : '0.00';

  const pools = [
    { key: 'govt', label: 'Govt Reserve', pct: t.govt_reserve_pct, amt: t.govt_reserve_amount, minted: t.govt_reserve_minted, color: 'from-red-500 to-orange-500', locked: true, until: 2040 },
    { key: 'promo', label: 'Promo Supply', pct: t.promo_supply_pct, amt: t.promo_supply_amount, minted: t.promo_supply_minted, color: 'from-blue-500 to-cyan-500' },
    { key: 'admin', label: 'Admin Supply', pct: t.admin_supply_pct, amt: t.admin_supply_amount, minted: t.admin_supply_minted, color: 'from-emerald-500 to-teal-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6">
        <div className="text-xs uppercase text-amber-300">Total Supply (Hard Cap)</div>
        <div className="text-4xl font-extrabold mt-2 text-amber-200">{t.total_supply.toLocaleString()} TSB</div>
        <div className="text-sm text-slate-300 mt-1">
          Minted: <strong>{t.total_minted.toLocaleString()}</strong> ({pct(t.total_minted, t.total_supply)}%) •
          Circulating: <strong>{t.circulating_supply.toLocaleString()}</strong>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {pools.map((p) => (
          <div key={p.key} className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">{p.label} ({p.pct}%)</div>
              {p.locked && <div className="text-[10px] bg-red-500/30 text-red-300 px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> till {p.until}</div>}
            </div>
            <div className="text-xl font-bold">{p.amt.toLocaleString()} TSB</div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden mt-3">
              <div className={`h-full bg-gradient-to-r ${p.color}`} style={{ width: `${pct(p.minted, p.amt)}%` }}></div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Minted: {p.minted.toLocaleString()} ({pct(p.minted, p.amt)}%)
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========= Users =========
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get(`/admin/tsb/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    setUsers(r.data.users || []);
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by username / email / phone"
          data-testid="tsb-admin-user-search"
          className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg"
        />
        <button onClick={load} className="px-4 py-2 bg-amber-500 text-black font-bold rounded-lg">Search</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tsb-admin-users-table">
          <thead className="bg-slate-800/50 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Username</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-right">Cash</th>
              <th className="px-3 py-2 text-right">TSB</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                <td className="px-3 py-2">{u.username}</td>
                <td className="px-3 py-2 text-slate-400">{u.email}</td>
                <td className="px-3 py-2"><span className="text-[10px] px-2 py-0.5 bg-slate-700 rounded">{u.role}</span></td>
                <td className="px-3 py-2 text-right text-emerald-300">₹{u.cash_balance.toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-amber-300">{u.tsb_balance.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========= Adjust (Maker-Checker) =========
const AdjustTab = () => {
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState({ user_id: '', wallet: 'TSB', direction: 'credit', amount: '', reason: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/tsb/adjust/pending');
    setPending(r.data.requests || []);
  };

  const submit = async () => {
    if (!form.user_id || !form.amount || !form.reason) return toast.error('All fields required');
    try {
      await api.post('/admin/tsb/adjust/request', { ...form, amount: parseFloat(form.amount) });
      toast.success('Adjustment request created. Awaits checker approval.');
      setForm({ user_id: '', wallet: 'TSB', direction: 'credit', amount: '', reason: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/tsb/adjust/${id}/action`, { action });
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5">
        <h3 className="text-lg font-bold mb-1">Maker — Create Adjustment Request</h3>
        <p className="text-xs text-slate-400 mb-4">A second admin (checker) must approve before it applies.</p>
        <input
          placeholder="User ID"
          value={form.user_id}
          onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          data-testid="tsb-adjust-user-id"
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3"
        />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <select value={form.wallet} onChange={(e) => setForm({ ...form, wallet: e.target.value })} data-testid="tsb-adjust-wallet" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded">
            <option value="CASH">CASH</option>
            <option value="TSB">TSB</option>
          </select>
          <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="tsb-adjust-direction" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded">
            <option value="credit">Credit (+)</option>
            <option value="debit">Debit (-)</option>
          </select>
        </div>
        <input
          type="number"
          placeholder="Amount"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          data-testid="tsb-adjust-amount"
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3"
        />
        <textarea
          placeholder="Reason (audit trail)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          data-testid="tsb-adjust-reason"
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3"
        />
        <button onClick={submit} data-testid="tsb-adjust-submit" className="w-full py-2 bg-amber-500 text-black font-bold rounded">
          Submit for Approval
        </button>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3">Checker — Pending Requests ({pending.length})</h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="tsb-adjust-pending-list">
          {pending.map((p) => (
            <div key={p.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
              <div className="text-xs text-slate-400">Maker: <strong className="text-white">{p.maker_username}</strong></div>
              <div className="font-semibold mt-1">{p.direction.toUpperCase()} {p.amount} {p.wallet} → {p.user_username}</div>
              <div className="text-xs text-slate-300 italic">&quot;{p.reason}&quot;</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(p.id, 'approve')} data-testid={`tsb-adjust-approve-${p.id}`} className="flex-1 py-1.5 bg-emerald-600 rounded text-sm font-bold flex items-center justify-center gap-1"><Check className="w-4 h-4" /> Approve</button>
                <button onClick={() => act(p.id, 'reject')} data-testid={`tsb-adjust-reject-${p.id}`} className="flex-1 py-1.5 bg-red-600 rounded text-sm font-bold flex items-center justify-center gap-1"><X className="w-4 h-4" /> Reject</button>
              </div>
            </div>
          ))}
          {pending.length === 0 && <div className="text-slate-500 text-sm">No pending requests</div>}
        </div>
      </div>
    </div>
  );
};

// ========= Products =========
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ vendor_name: '', product_name: '', price_inr: '', stock: 100, category: '' });
  const [lastAdded, setLastAdded] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/tsb/products');
    setProducts(r.data.products || []);
  };

  const seed = async () => {
    try {
      const r = await api.post('/admin/tsb/seed-products');
      toast.success(`Seeded ${r.data.inserted} products`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const add = async () => {
    if (!form.vendor_name || !form.product_name || !form.price_inr) return toast.error('All fields required');
    try {
      const r = await api.post('/admin/tsb/products', { ...form, price_inr: parseFloat(form.price_inr), stock: parseInt(form.stock) || 100 });
      const p = r.data?.product || {};
      toast.success(`✅ "${p.product_name}" added!`);
      setLastAdded({ ...p, at: new Date().toLocaleTimeString() });
      setForm({ vendor_name: '', product_name: '', price_inr: '', stock: 100, category: '' });
      load();
      setTimeout(() => setLastAdded(null), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggle = async (p) => {
    await api.put(`/admin/tsb/products/${p.id}`, { is_active: !p.is_active });
    load();
  };

  return (
    <div className="space-y-6">
      {lastAdded && (
        <div className="bg-emerald-500/20 border-2 border-emerald-400 rounded-xl p-4 flex items-center gap-3" data-testid="product-success-banner">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-2xl">✅</div>
          <div className="flex-1">
            <div className="font-bold text-emerald-200">SUCCESS — Product Added</div>
            <div className="text-xs text-emerald-300/80">{lastAdded.vendor_name} • {lastAdded.product_name} • ₹{lastAdded.price_inr} • Stock {lastAdded.stock} • added at {lastAdded.at}</div>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <button onClick={seed} data-testid="tsb-admin-seed-btn" className="px-4 py-2 bg-purple-600 rounded font-bold">Seed Default Products</button>
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5">
        <h3 className="text-base font-bold mb-3">Add Product</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <input placeholder="Vendor" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" data-testid="tsb-add-vendor" />
          <input placeholder="Product" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" data-testid="tsb-add-product" />
          <input type="number" placeholder="Price ₹" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" data-testid="tsb-add-price" />
          <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" data-testid="tsb-add-stock" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" data-testid="tsb-add-category" />
        </div>
        <button onClick={add} data-testid="tsb-add-submit" className="mt-3 px-4 py-2 bg-amber-500 text-black font-bold rounded">Add Product</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/50 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-right">Stock</th>
              <th className="px-3 py-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-slate-800">
                <td className="px-3 py-2">{p.vendor_name}</td>
                <td className="px-3 py-2">{p.product_name}</td>
                <td className="px-3 py-2 text-right text-amber-300">₹{p.price_inr}</td>
                <td className="px-3 py-2 text-right">{p.stock}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggle(p)} className={`px-3 py-1 rounded text-xs font-bold ${p.is_active ? 'bg-emerald-600' : 'bg-slate-600'}`}>
                    {p.is_active ? 'ON' : 'OFF'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========= Settings (Rate) =========
const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [a, b] = await Promise.all([api.get('/admin/tsb/settings'), api.get('/admin/tsb/rate/history')]);
    setS(a.data);
    setHistory(b.data.history || []);
  };

  const updateRate = async () => {
    const r = parseFloat(newRate);
    if (!r || r <= 0) return toast.error('Rate must be > 0');
    try {
      await api.put('/admin/tsb/rate', { new_rate: r });
      toast.success(`Rate updated to ₹${r}`);
      setNewRate('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveSettings = async (updates) => {
    try {
      await api.put('/admin/tsb/settings', updates);
      toast.success('Saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!s) return <div>Loading...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-slate-800/40 border border-amber-500/30 rounded-2xl p-5">
        <h3 className="text-lg font-bold mb-2">Live TSB Rate</h3>
        <p className="text-xs text-slate-400 mb-3">Affects conversion + 50/50 checkout calculations across the entire platform.</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <div className="text-xs text-slate-400">Current Rate</div>
            <div className="text-2xl font-bold text-amber-300">1 TSB = ₹{s.current_tsb_rate}</div>
          </div>
          <input
            type="number"
            step="0.01"
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            placeholder="New rate"
            data-testid="tsb-admin-rate-input"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded w-32"
          />
          <button onClick={updateRate} data-testid="tsb-admin-rate-update" className="px-4 py-2 bg-amber-500 text-black font-bold rounded">Update</button>
        </div>
      </div>

      <div className="bg-slate-800/40 border border-amber-500/30 rounded-2xl p-5" data-testid="tsb-admin-welcome-bonus">
        <h3 className="text-base font-bold mb-2 flex items-center gap-2">🎁 Welcome Bonus</h3>
        <p className="text-xs text-slate-400 mb-3">हर नए user signup पर admin pool से auto-mint होकर TSB credit होगा। Hard cap honored.</p>
        <div className="flex flex-wrap items-end gap-4">
          <Toggle label="Enable Welcome Bonus" checked={!!s.welcome_bonus_enabled} onChange={(v) => saveSettings({ welcome_bonus_enabled: v })} testid="tsb-toggle-welcome-bonus" />
          <NumField label="Bonus Amount (TSB)" value={s.welcome_bonus_amount || 0} onChange={(v) => saveSettings({ welcome_bonus_amount: v })} step="0.01" />
        </div>
      </div>

      <div className="bg-slate-800/40 border border-blue-500/30 rounded-2xl p-5" data-testid="tsb-admin-explorer-privacy">
        <h3 className="text-base font-bold mb-2 flex items-center gap-2">🔍 Public Explorer Privacy</h3>
        <p className="text-xs text-slate-400 mb-3">
          Public TSB Explorer (`/explorer`) पर usernames masked दिखते हैं (जैसे <code className="bg-slate-900 px-1 rounded">@hea***est</code>)। Full disclosure on करने से असली usernames + names दिखेंगे।
        </p>
        <Toggle
          label="Full Disclosure Mode (show real usernames + names on explorer)"
          checked={!!s.explorer_full_disclosure}
          onChange={(v) => saveSettings({ explorer_full_disclosure: v })}
          testid="tsb-toggle-explorer-privacy"
        />
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5">
        <h3 className="text-base font-bold mb-3">Module Toggles</h3>
        <div className="flex flex-wrap gap-6">
          <Toggle label="Marketplace" checked={!!s.is_marketplace_enabled} onChange={(v) => saveSettings({ is_marketplace_enabled: v })} testid="tsb-toggle-marketplace" />
          <Toggle label="Conversion (Cash→TSB)" checked={!!s.is_conversion_enabled} onChange={(v) => saveSettings({ is_conversion_enabled: v })} testid="tsb-toggle-conversion" />
          <Toggle label="P2P Transfer" checked={!!s.is_p2p_enabled} onChange={(v) => saveSettings({ is_p2p_enabled: v })} testid="tsb-toggle-p2p" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <NumField label="Min Conversion ₹" value={s.min_conversion} onChange={(v) => saveSettings({ min_conversion: v })} step="0.01" />
          <NumField label="Min P2P (TSB)" value={s.min_p2p} onChange={(v) => saveSettings({ min_p2p: v })} step="0.0000001" />
          <NumField label="Min Deposit ₹" value={s.min_deposit} onChange={(v) => saveSettings({ min_deposit: v })} step="0.01" />
        </div>
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5">
        <h3 className="text-base font-bold mb-3 flex items-center gap-2">💳 Deposit Payment Details</h3>
        <p className="text-xs text-slate-400 mb-3">These are shown to users on the TSB → Deposit tab.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextSetting label="UPI ID" value={s.deposit_upi_id} onSave={(v) => saveSettings({ deposit_upi_id: v })} testid="tsb-set-upi-id" />
          <TextSetting label="UPI QR Image URL" value={s.deposit_upi_qr_url} onSave={(v) => saveSettings({ deposit_upi_qr_url: v })} testid="tsb-set-upi-qr" />
          <TextSetting label="Bank Name" value={s.deposit_bank_name} onSave={(v) => saveSettings({ deposit_bank_name: v })} testid="tsb-set-bank-name" />
          <TextSetting label="Account Holder" value={s.deposit_bank_holder} onSave={(v) => saveSettings({ deposit_bank_holder: v })} testid="tsb-set-bank-holder" />
          <TextSetting label="Bank Account No." value={s.deposit_bank_account} onSave={(v) => saveSettings({ deposit_bank_account: v })} testid="tsb-set-bank-account" />
          <TextSetting label="IFSC" value={s.deposit_bank_ifsc} onSave={(v) => saveSettings({ deposit_bank_ifsc: v })} testid="tsb-set-bank-ifsc" />
          <TextSetting label="WhatsApp (with country code, no +)" value={s.deposit_support_whatsapp} onSave={(v) => saveSettings({ deposit_support_whatsapp: v })} testid="tsb-set-whatsapp" />
          <TextSetting label="TSB Coin Logo URL" value={s.coin_logo_url} onSave={(v) => saveSettings({ coin_logo_url: v })} testid="tsb-set-logo" />
        </div>
        <label className="text-xs text-slate-400 mt-3 block">Extra Instructions (multiline)</label>
        <textarea defaultValue={s.deposit_instructions || ''} onBlur={(e) => saveSettings({ deposit_instructions: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mt-1 text-sm" />
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5">
        <h3 className="text-base font-bold mb-3 flex items-center gap-2"><Download className="w-4 h-4" /> Ledger Export</h3>
        <p className="text-xs text-slate-400 mb-3">Download complete transaction ledger as CSV (opens in Excel/Word).</p>
        <button
          onClick={async () => {
            try {
              const baseUrl = process.env.REACT_APP_BACKEND_URL;
              const token = localStorage.getItem('token');
              const r = await fetch(`${baseUrl}/api/admin/tsb/ledger/export`, { headers: { Authorization: `Bearer ${token}` } });
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `tsb_admin_ledger_${new Date().toISOString().slice(0,10)}.csv`;
              a.click(); URL.revokeObjectURL(url);
              toast.success('Downloaded');
            } catch { toast.error('Download failed'); }
          }}
          data-testid="tsb-admin-ledger-download"
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-bold flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> Download Full Ledger CSV
        </button>
      </div>

      <div>
        <h3 className="text-base font-bold mb-3">Rate History</h3>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {history.map((h) => (
            <div key={h.id} className="bg-slate-800/40 border border-slate-700 rounded p-2 text-xs">
              <strong className="text-amber-300">₹{h.old_rate} → ₹{h.new_rate}</strong> by {h.changed_by} on {new Date(h.changed_at).toLocaleString()}
              {h.note && <span className="ml-2 text-slate-400 italic">&quot;{h.note}&quot;</span>}
            </div>
          ))}
          {history.length === 0 && <div className="text-slate-500 text-sm">No history yet</div>}
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer" data-testid={testid}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
    <span className="text-sm">{label}</span>
  </label>
);

const TextSetting = ({ label, value, onSave, testid }) => {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div>
      <label className="text-xs text-slate-400">{label}</label>
      <div className="flex gap-1 mt-1">
        <input
          type="text" value={v}
          onChange={(e) => setV(e.target.value)}
          onBlur={() => { if (v !== (value || '')) onSave(v); }}
          data-testid={testid}
          className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm"
        />
      </div>
    </div>
  );
};

const NumField = ({ label, value, onChange, step = '1' }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div>
      <label className="text-xs text-slate-400">{label}</label>
      <input
        type="number" step={step} value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { const n = parseFloat(v); if (!isNaN(n) && n !== value) onChange(n); }}
        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm mt-1"
      />
    </div>
  );
};

// ========= Admin Deposits Queue =========
const DepositsTab = () => {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [a, b] = await Promise.all([
      api.get('/admin/tsb/deposits?status=pending'),
      api.get('/admin/tsb/deposits'),
    ]);
    setPending(a.data?.requests || []);
    setHistory((b.data?.requests || []).filter(r => r.status !== 'pending'));
  };

  const act = async (id, action, creditAs = 'cash') => {
    try {
      await api.post(`/admin/tsb/deposits/${id}/action`, { action, credit_as: creditAs });
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-bold mb-3">Pending Deposits ({pending.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="tsb-admin-pending-deposits">
          {pending.map((d) => (
            <div key={d.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
              <div className="text-xs text-slate-400">User: <strong className="text-white">{d.username}</strong></div>
              <div className="font-bold mt-1">₹{d.amount} via {d.payment_method}</div>
              {d.payment_proof && <div className="text-xs text-slate-300">Ref: <span className="font-mono">{d.payment_proof}</span></div>}
              {d.note && <div className="text-xs text-slate-400 italic">&quot;{d.note}&quot;</div>}
              <div className="text-[10px] text-slate-500">{new Date(d.created_at).toLocaleString()}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(d.id, 'approve', 'cash')} className="flex-1 py-1.5 bg-emerald-600 rounded text-xs font-bold">Approve → Cash</button>
                <button onClick={() => act(d.id, 'approve', 'tsb')} className="flex-1 py-1.5 bg-amber-600 rounded text-xs font-bold">Approve → TSB</button>
                <button onClick={() => act(d.id, 'reject')} className="px-3 py-1.5 bg-red-600 rounded text-xs font-bold">Reject</button>
              </div>
            </div>
          ))}
          {pending.length === 0 && <div className="text-slate-500 text-sm">No pending deposits</div>}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-bold mb-3">Recent History</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {history.slice(0, 30).map((d) => (
            <div key={d.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span>{d.username} • ₹{d.amount}</span>
                <span className={`text-xs px-2 rounded ${d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{d.status} {d.credit_as && `(${d.credit_as})`}</span>
              </div>
              <div className="text-[10px] text-slate-500">{new Date(d.processed_at || d.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ========= Mint Powers =========
const MintTab = () => {
  const [form, setForm] = useState({ pool: 'admin', amount: '', destination: 'user', user_id: '', reason: '' });
  const [reserve, setReserve] = useState(0);
  const [settings, setSettings] = useState(null);
  const [lastMint, setLastMint] = useState(null);  // success banner

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [r, s] = await Promise.all([
      api.get('/admin/tsb/mint/circulation'),
      api.get('/admin/tsb/settings'),
    ]);
    setReserve(r.data?.balance || 0);
    setSettings(s.data);
  };

  const mint = async () => {
    const a = parseFloat(form.amount);
    if (!a || a <= 0) return toast.error('Invalid amount');
    if (form.destination === 'user' && !form.user_id) return toast.error('User ID required');
    try {
      await api.post('/admin/tsb/mint', { ...form, amount: a });
      const prev = reserve;
      await load();
      const summary = {
        amount: a, pool: form.pool, destination: form.destination,
        user_id: form.user_id, at: new Date().toLocaleTimeString(),
        oldReserve: prev,
      };
      setLastMint(summary);
      toast.success(`✅ Minted ${a} TSB successfully!`);
      setForm({ pool: 'admin', amount: '', destination: 'user', user_id: '', reason: '' });
      setTimeout(() => setLastMint(null), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Mint failed'); }
  };

  const saveAuto = async () => {
    try {
      await api.put('/admin/tsb/settings', {
        auto_mint_enabled: !!settings.auto_mint_enabled,
        auto_mint_amount_per_day: parseFloat(settings.auto_mint_amount_per_day || 0),
        auto_mint_pool: settings.auto_mint_pool || 'admin',
      });
      toast.success('Auto-circulation settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {lastMint && (
        <div className="bg-emerald-500/20 border-2 border-emerald-400 rounded-xl p-4 flex items-center gap-3" data-testid="mint-success-banner">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-2xl">✅</div>
          <div className="flex-1">
            <div className="font-bold text-emerald-200">SUCCESS — {lastMint.amount} TSB minted</div>
            <div className="text-xs text-emerald-300/80">
              Pool: <strong>{lastMint.pool}</strong> → Destination: <strong>{lastMint.destination === 'user' ? `user ${lastMint.user_id.slice(0, 8)}` : 'Circulation Reserve'}</strong> at {lastMint.at}
            </div>
            <div className="text-xs text-emerald-300/80">
              Circulation Reserve: <strong>{lastMint.oldReserve.toLocaleString()} → {reserve.toLocaleString()} TSB</strong>
            </div>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5" data-testid="tsb-admin-manual-mint">
        <h3 className="text-lg font-bold mb-3">🪙 Manual Mint</h3>
        <p className="text-xs text-slate-400 mb-3">Mint TSB from a supply pool. Enforces 10B hard cap automatically.</p>
        <select value={form.pool} onChange={(e) => setForm({ ...form, pool: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3">
          <option value="admin">Admin Pool (5B)</option>
          <option value="promo">Promo Pool (2B)</option>
          <option value="govt">Govt Reserve (locked till 2040)</option>
        </select>
        <input type="number" step="0.0000001" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3" />
        <select value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3">
          <option value="user">Credit to User</option>
          <option value="circulation">Add to Circulation Reserve</option>
        </select>
        {form.destination === 'user' && (
          <input type="text" placeholder="User ID" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3" />
        )}
        <textarea placeholder="Reason (audit)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3" rows={2} />
        <button onClick={mint} className="w-full py-2 bg-purple-600 rounded font-bold">Mint Now</button>
        <div className="mt-3 text-xs text-slate-400">Current Circulation Reserve: <strong className="text-amber-300">{reserve.toLocaleString()} TSB</strong></div>
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-5" data-testid="tsb-admin-auto-mint">
        <h3 className="text-lg font-bold mb-3">🔄 Auto-Circulation</h3>
        <p className="text-xs text-slate-400 mb-3">Background scheduler mints fixed daily amount automatically (placeholder — runs on next admin trigger).</p>
        <label className="flex items-center gap-2 mb-3">
          <input type="checkbox" checked={!!settings?.auto_mint_enabled} onChange={(e) => setSettings({ ...settings, auto_mint_enabled: e.target.checked })} />
          <span className="text-sm">Enable Auto-Mint</span>
        </label>
        <label className="text-xs text-slate-400">Daily mint amount (TSB)</label>
        <input type="number" step="0.0000001" value={settings?.auto_mint_amount_per_day || 0} onChange={(e) => setSettings({ ...settings, auto_mint_amount_per_day: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3" />
        <label className="text-xs text-slate-400">From pool</label>
        <select value={settings?.auto_mint_pool || 'admin'} onChange={(e) => setSettings({ ...settings, auto_mint_pool: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-3">
          <option value="admin">Admin Pool</option>
          <option value="promo">Promo Pool</option>
        </select>
        <div className="text-xs text-slate-500 mb-3">Last auto-run: {settings?.auto_mint_last_run || '—'}</div>
        <button onClick={saveAuto} className="w-full py-2 bg-amber-500 text-black rounded font-bold">Save Auto-Mint Settings</button>
      </div>
    </div>
    </div>
  );
};

// ========= Roadmap =========
const RoadmapTab = () => {
  const [levels, setLevels] = useState([]);
  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/tsb/roadmap');
    setLevels(r.data?.roadmap_levels || []);
  };
  const update = (i, key, val) => {
    const newLevels = [...levels];
    newLevels[i] = { ...newLevels[i], [key]: key === 'rate_inr' ? (val === '' ? null : parseFloat(val)) : val };
    setLevels(newLevels);
  };
  const save = async () => {
    try {
      await api.put('/admin/tsb/roadmap', { roadmap_levels: levels });
      toast.success('Roadmap saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">Define rate levels with date ranges. Active level is auto-detected on the user Overview page.</p>
      {levels.map((lvl, i) => (
        <div key={i} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
          <div className="font-bold text-amber-300">Level {lvl.level}</div>
          <input type="date" value={lvl.start} onChange={(e) => update(i, 'start', e.target.value)} className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-sm" />
          <input type="date" value={lvl.end} onChange={(e) => update(i, 'end', e.target.value)} className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-sm" />
          <input type="number" step="0.01" placeholder="Rate INR (blank = TBD)" value={lvl.rate_inr ?? ''} onChange={(e) => update(i, 'rate_inr', e.target.value)} className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-sm" />
        </div>
      ))}
      <button onClick={save} data-testid="tsb-admin-roadmap-save" className="px-4 py-2 bg-amber-500 text-black font-bold rounded">Save Roadmap</button>
    </div>
  );
};

// ========= Vendors Management =========
const VendorsTab = () => {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const [p, a] = await Promise.all([
      api.get('/admin/tsb/vendors?status=pending'),
      api.get('/admin/tsb/vendors?status=approved'),
    ]);
    setPending(p.data?.vendors || []);
    setApproved(a.data?.vendors || []);
  };

  const act = async (id, action) => {
    const note = action === 'reject' ? (window.prompt('Rejection reason (optional)?') || '') : '';
    try {
      await api.post(`/admin/tsb/vendors/${id}/action`, { action, note });
      toast.success(`${action.toUpperCase()}D`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">⏳ Pending Vendor Applications ({pending.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="admin-vendors-pending">
          {pending.map((v) => (
            <div key={v.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
              <div className="font-bold text-amber-300">{v.business_name}</div>
              <div className="text-xs text-slate-400">User: <strong className="text-white">{v.user_username}</strong> ({v.user_email})</div>
              {v.contact_phone && <div className="text-xs">Phone: {v.contact_phone}</div>}
              {v.gst && <div className="text-xs">GST: {v.gst}</div>}
              {v.address && <div className="text-xs text-slate-400">{v.address}</div>}
              {v.description && <div className="text-xs italic text-slate-300 mt-1">&quot;{v.description}&quot;</div>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(v.id, 'approve')} data-testid={`admin-vendor-approve-${v.id}`} className="flex-1 py-1.5 bg-emerald-600 rounded text-xs font-bold">Approve</button>
                <button onClick={() => act(v.id, 'reject')} className="flex-1 py-1.5 bg-red-600 rounded text-xs font-bold">Reject</button>
              </div>
            </div>
          ))}
          {pending.length === 0 && <div className="text-slate-500 text-sm">No pending applications</div>}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">✅ Approved Vendors ({approved.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="admin-vendors-approved">
          {approved.map((v) => (
            <div key={v.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold">{v.business_name}</div>
                  <div className="text-xs text-slate-400">{v.user_username}</div>
                </div>
                <button onClick={() => act(v.id, 'suspend')} className="text-xs px-2 py-1 bg-orange-600 rounded">Suspend</button>
              </div>
            </div>
          ))}
          {approved.length === 0 && <div className="text-slate-500 text-sm">No approved vendors yet</div>}
        </div>
      </div>
    </div>
  );
};

export default TSBAdmin;
